import React, { useState, useEffect, useMemo } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  fetchLevelingReadiness,
  fetchLevelingLines,
  submitLevelingAwards
} from '../services/procurementBidsClient';
import {
  ArrowLeft,
  CheckCircle,
  Download,
  Save,
  Clock,
  AlertCircle,
  ChevronRight,
  Maximize2,
  Minimize2
} from 'lucide-react';

export default function ProcurementLeveling({ packageId, onNavigate }) {

  const [step, setStep] = useState('box-score'); // 'box-score' | 'leveling'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readiness, setReadiness] = useState(null);

  const [levelingData, setLevelingData] = useState({ lines: [], awards: [] });
  const [awardedVendorId, setAwardedVendorId] = useState(null); // single vendor award
  const [saving, setSaving] = useState(false);
  const [focusMode, setFocusMode] = useState(false);

  useEffect(() => {
    async function init() {
      try {
        setLoading(true);
        const readinessData = await fetchLevelingReadiness(packageId);
        setReadiness(readinessData);

        const levelingLines = await fetchLevelingLines(packageId);
        setLevelingData(levelingLines);

        // Prepopulate existing vendor-level award if any
        if (levelingLines.awards?.length > 0) {
          setAwardedVendorId(levelingLines.awards[0].vendor_id);
        }

      } catch (err) {
        setError(err.message || 'Failed to load leveling context.');
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [packageId]);

  // Restructure lines by Scope -> LineSequence -> Vendor
  const comparisonMatrix = useMemo(() => {
    const scopes = {};
    for (const line of levelingData.lines) {
      const scopeId = line.scope_id_candidate || 'unknown';
      const seq = line.line_sequence || 0;

      if (!scopes[scopeId]) {
        scopes[scopeId] = {
          scopeLabel: line.raw_scope_label || scopeId,
          lines: {}
        };
      }

      if (!scopes[scopeId].lines[seq]) {
        scopes[scopeId].lines[seq] = {
          line_sequence: seq,
          description: line.raw_description,
          vendors: {}
        };
      }

      scopes[scopeId].lines[seq].vendors[line.vendor_id] = line;
    }
    return scopes;
  }, [levelingData.lines]);

  const uniqueVendors = useMemo(() => {
    const vMap = {};
    for (const line of levelingData.lines) {
      if (!vMap[line.vendor_id]) {
        vMap[line.vendor_id] = line.vendor_name;
      }
    }
    return Object.entries(vMap).map(([id, name]) => ({ id, name }));
  }, [levelingData.lines]);

  // Per-vendor grand totals
  const vendorTotals = useMemo(() => {
    const totals = {};
    for (const line of levelingData.lines) {
      if (!totals[line.vendor_id]) totals[line.vendor_id] = 0;
      totals[line.vendor_id] += parseFloat(line.submitted_amount || 0);
    }
    return totals;
  }, [levelingData.lines]);

  const handleSelectVendorAward = (vendorId) => {
    setAwardedVendorId(prev => prev === vendorId ? null : vendorId);
  };

  const handleSaveAwards = async () => {
    try {
      setSaving(true);
      if (!awardedVendorId) {
        alert('Please select an awarded vendor before committing.');
        return;
      }
      // Build one award record per line belonging to the awarded vendor
      const awardsArray = levelingData.lines
        .filter(l => l.vendor_id === awardedVendorId)
        .map(l => ({
          scope_id: l.scope_id_candidate,
          line_sequence: l.line_sequence,
          response_id: l.response_id,
          line_id: l.line_id,
          vendor_id: l.vendor_id,
          awarded_amount: l.submitted_amount,
          awarded_rate: l.submitted_rate,
          awarded_quantity: l.submitted_quantity,
          awarded_uom: l.submitted_uom,
        }));
      await submitLevelingAwards(packageId, { awards: awardsArray });
      alert('Awards saved successfully!');
    } catch (err) {
      alert(`Failed to save awards: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Leveling Matrix');

    // Define columns
    const columns = [
      { header: 'Scope', key: 'scope', width: 20 },
      { header: 'Description', key: 'description', width: 40 },
    ];

    uniqueVendors.forEach(vendor => {
      columns.push(
        { header: `${vendor.name} - Rate`, key: `${vendor.id}_rate`, width: 15 },
        { header: `${vendor.name} - Total`, key: `${vendor.id}_total`, width: 15 },
        { header: `${vendor.name} - Notes`, key: `${vendor.id}_notes`, width: 30 }
      );
    });

    worksheet.columns = columns;

    // Title Row
    const titleRow = worksheet.insertRow(1, [`Bid Leveling Matrix - Package: ${packageId}`]);
    titleRow.font = { size: 16, bold: true };
    titleRow.height = 30;
    worksheet.mergeCells(1, 1, 1, columns.length);

    // Spacer
    worksheet.addRow([]);

    // Grand Total Row
    const totalRowData = ['GRAND TOTAL', ''];
    uniqueVendors.forEach(vendor => {
      const total = vendorTotals[vendor.id] || 0;
      const isAwarded = awardedVendorId === vendor.id;
      totalRowData.push('', total, isAwarded ? '✓ AWARDED' : '');
    });
    const totalRow = worksheet.addRow(totalRowData);
    totalRow.font = { bold: true };
    totalRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF0F172A' } // Dark blue
    };
    totalRow.font = { color: { argb: 'FFFFFFFF' }, bold: true };
    
    // Apply currency formatting to totals
    uniqueVendors.forEach((_, idx) => {
      const totalCell = totalRow.getCell(4 + (idx * 3));
      totalCell.numFmt = '"$"#,##0.00';
    });

    worksheet.addRow([]); // Spacer

    // Header Row (Vendors)
    const headerRowData = ['Scope', 'Description'];
    uniqueVendors.forEach(vendor => {
      headerRowData.push('Rate', 'Total', 'Notes');
    });
    const headerRow = worksheet.addRow(headerRowData);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1E293B' } // Lighter dark blue
    };
    headerRow.font = { color: { argb: 'FFCBD5E1' }, bold: true };

    // Data Rows
    for (const [scopeId, scopeData] of Object.entries(comparisonMatrix)) {
      // Scope Header row
      const scopeHeaderRow = worksheet.addRow([scopeData.scopeLabel]);
      scopeHeaderRow.font = { bold: true };
      scopeHeaderRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0F2FE' } // Light Blue (ColorBrewer inspired)
      };
      worksheet.mergeCells(scopeHeaderRow.number, 1, scopeHeaderRow.number, columns.length);

      for (const [seq, lineData] of Object.entries(scopeData.lines)) {
        const rowData = [scopeData.scopeLabel, lineData.description];
        uniqueVendors.forEach(vendor => {
          const vData = lineData.vendors[vendor.id];
          rowData.push(
            vData?.submitted_rate || 0,
            vData?.submitted_amount || 0,
            vData?.raw_notes || ''
          );
        });
        const dataRow = worksheet.addRow(rowData);
        
        // Formatting
        uniqueVendors.forEach((_, idx) => {
          const rateCell = dataRow.getCell(3 + (idx * 3));
          const totalCell = dataRow.getCell(4 + (idx * 3));
          rateCell.numFmt = '"$"#,##0.00';
          totalCell.numFmt = '"$"#,##0.00';
        });
      }
    }

    // Auto-filter and freeze top rows
    worksheet.views = [{ state: 'frozen', xSplit: 2, ySplit: 5 }];

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Bid_Leveling_${packageId}.xlsx`);
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <h2 style={{ color: '#E2E8F0', marginTop: '1rem', fontFamily: 'Inter, sans-serif' }}>Syncing Procurement Reality...</h2>
      </div>
    );
  }

  if (error) {
    return <div style={styles.errorContainer}>Error: {error}</div>;
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={() => onNavigate('procurement-review')} style={styles.backButton}>
            <ArrowLeft size={20} />
          </button>
          <div style={styles.headerTitles}>
            <h1 style={styles.pageTitle}>Bid Leveling</h1>
            <p style={styles.pageSubtitle}>{readiness?.package_title}</p>
          </div>
        </div>
      </header>

      {step === 'box-score' && (
        <div style={styles.boxScoreWrapper}>
          <div style={styles.glassCard}>
            <h2 style={styles.cardTitle}>Leveling Readiness</h2>

            <div style={styles.statsGrid}>
              <div style={styles.statBox}>
                <span style={styles.statLabel}>RFPs Sent</span>
                <span style={styles.statValue}>{readiness?.requested_vendors_count}</span>
              </div>
              <div style={styles.statBox}>
                <span style={styles.statLabel}>Responses Received</span>
                <span style={styles.statValueHighlight}>{readiness?.responses_received_count}</span>
              </div>
            </div>

            <div style={styles.statusBanner(readiness?.readiness_status)}>
              {readiness?.readiness_status === 'ready' ? (
                <><CheckCircle size={24} /> <span style={{ marginLeft: '0.5rem' }}>Sufficient responses received. Ready for leveling.</span></>
              ) : (
                <><Clock size={24} /> <span style={{ marginLeft: '0.5rem' }}>Waiting on outstanding responses. You can proceed, but the matrix is incomplete.</span></>
              )}
            </div>

            <button
              style={styles.primaryButton}
              onClick={() => setStep('leveling')}
            >
              Begin Leveling <ChevronRight size={20} style={{ marginLeft: '0.5rem' }} />
            </button>
          </div>
        </div>
      )}

      {step === 'leveling' && (
        <div style={styles.levelingWrapper}>
          <div style={styles.toolbar}>
            <h2 style={styles.matrixTitle}>Comparison Matrix</h2>
            <div style={styles.toolbarActions}>
              <button
                onClick={() => setFocusMode(!focusMode)}
                style={focusMode ? styles.activeFocusButton : styles.outlineButton}
                title={focusMode ? "Exit Focus Mode" : "Enter Focus Mode (Hide detail columns)"}
              >
                {focusMode ? <Minimize2 size={18} style={{ marginRight: '0.5rem' }} /> : <Maximize2 size={18} style={{ marginRight: '0.5rem' }} />}
                {focusMode ? "Exit Focus" : "Focus Mode"}
              </button>
              <button onClick={handleExportExcel} style={styles.outlineButton}>
                <Download size={18} style={{ marginRight: '0.5rem' }} />
                Export to Excel
              </button>
              <button
                onClick={handleSaveAwards}
                style={styles.primarySettingsButton}
                disabled={saving}
              >
                <Save size={18} style={{ marginRight: '0.5rem' }} />
                {saving ? 'Saving...' : 'Commit Awards'}
              </button>
            </div>
          </div>

          <div style={styles.matrixContainer}>
            <table style={styles.matrixTable}>
              <thead>
                <tr>
                  <th style={{
                    ...styles.matrixTh,
                    width: focusMode ? '150px' : '300px'
                  }}>Base Scope Item</th>
                  {uniqueVendors.map(vendor => {
                    const isAwarded = awardedVendorId === vendor.id;
                    const total = vendorTotals[vendor.id] || 0;
                    return (
                      <th key={vendor.id} style={{
                        ...styles.matrixVendorTh,
                        backgroundColor: isAwarded ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                        boxShadow: isAwarded ? 'inset 0 -3px 0 #10B981' : 'none',
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <span>{vendor.name}</span>
                          <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#34D399' }}>
                            ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <button
                            onClick={() => handleSelectVendorAward(vendor.id)}
                            style={{
                              background: isAwarded ? '#10B981' : 'rgba(255,255,255,0.05)',
                              border: `1px solid ${isAwarded ? '#10B981' : 'rgba(255,255,255,0.15)'}`,
                              color: isAwarded ? '#fff' : '#94A3B8',
                              borderRadius: '6px',
                              padding: '0.3rem 0.75rem',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.4rem',
                              width: 'fit-content',
                              transition: 'all 0.2s',
                            }}
                          >
                            <div style={{
                              width: '10px', height: '10px', borderRadius: '50%',
                              border: isAwarded ? '3px solid #fff' : '2px solid #64748B',
                            }} />
                            {isAwarded ? 'Awarded' : 'Award This Vendor'}
                          </button>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {Object.entries(comparisonMatrix).map(([scopeId, scopeData]) => (
                  <React.Fragment key={scopeId}>
                    <tr style={styles.scopeHeaderRow}>
                      <td colSpan={uniqueVendors.length + 1} style={styles.scopeHeaderCell}>
                        <div style={{ display: 'flex', alignItems: 'center gap: 0.75rem' }}>
                          <span style={{ fontWeight: '700' }}>{scopeData.scopeLabel}</span>
                          {focusMode && <span style={{ fontSize: '0.75rem', opacity: 0.6, fontWeight: 'normal' }}> (Focus Mode Active)</span>}
                        </div>
                      </td>
                    </tr>
                    {Object.entries(scopeData.lines).map(([seq, lineData]) => {
                      return (
                        <tr key={seq} style={styles.lineRow}>
                          <td style={{
                            ...styles.baseItemCell,
                            width: focusMode ? '150px' : '300px'
                          }}>
                            {focusMode ? (
                              <div style={{ ...styles.baseItemDesc, fontSize: '0.85rem', fontWeight: '600' }}>{scopeData.scopeLabel} Item</div>
                            ) : (
                              <>
                                <div style={styles.baseItemDesc}>{lineData.description}</div>
                                <div style={styles.baseItemSeq}>Seq: {seq}</div>
                              </>
                            )}
                          </td>

                          {uniqueVendors.map(vendor => {
                            const vData = lineData.vendors[vendor.id];
                            const isAwarded = awardedVendorId === vendor.id;

                            if (!vData) {
                              return (
                                <td key={vendor.id} style={styles.vendorCellEmpty}>
                                  <span style={styles.noBidText}>No Bid</span>
                                </td>
                              );
                            }

                            return (
                              <td
                                key={vendor.id}
                                style={isAwarded ? styles.vendorCellSelected : styles.vendorCell}
                              >
                                <div style={styles.vendorValTotal}>
                                  ${parseFloat(vData.submitted_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                {!focusMode && (
                                  <div style={styles.vendorValDetail}>
                                    {vData.submitted_quantity} {vData.submitted_basis} @ ${vData.submitted_rate}
                                  </div>
                                )}
                                {vData.raw_notes && (
                                  <div style={styles.vendorNotes}>
                                    <AlertCircle size={12} style={{ marginRight: '4px', flexShrink: 0 }} />
                                    <span>{vData.raw_notes}</span>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ----- Rich Aesthetics Inline Styles ----- //
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0B1120',
    color: '#F8FAFC',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '2rem',
    background: 'radial-gradient(ellipse at top, #1E293B, #0B1120)',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: '#0B1120',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    borderTopColor: '#3B82F6',
    animation: 'spin 1s ease-in-out infinite',
  },
  errorContainer: {
    color: '#EF4444',
    padding: '2rem',
    textAlign: 'center',
    fontSize: '1.25rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '3rem',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem',
  },
  backButton: {
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    color: '#F8FAFC',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  headerTitles: {
    display: 'flex',
    flexDirection: 'column',
  },
  pageTitle: {
    fontSize: '2rem',
    fontWeight: '700',
    margin: 0,
    background: 'linear-gradient(to right, #60A5FA, #A78BFA)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  pageSubtitle: {
    margin: 0,
    color: '#94A3B8',
    fontSize: '0.9rem',
    marginTop: '0.25rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  boxScoreWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '4rem',
  },
  glassCard: {
    background: 'rgba(30, 41, 59, 0.7)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '24px',
    padding: '3rem',
    width: '100%',
    maxWidth: '600px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  cardTitle: {
    fontSize: '1.5rem',
    margin: '0 0 2rem 0',
    color: '#E2E8F0',
    fontWeight: '600',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '2rem',
    width: '100%',
    marginBottom: '2rem',
  },
  statBox: {
    background: 'rgba(15, 23, 42, 0.5)',
    borderRadius: '16px',
    padding: '2rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    border: '1px solid rgba(255, 255, 255, 0.05)',
  },
  statLabel: {
    color: '#94A3B8',
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.5rem',
  },
  statValue: {
    fontSize: '3rem',
    fontWeight: '700',
    color: '#E2E8F0',
  },
  statValueHighlight: {
    fontSize: '3rem',
    fontWeight: '700',
    color: '#34D399',
    textShadow: '0 0 20px rgba(52, 211, 153, 0.4)',
  },
  statusBanner: (status) => ({
    width: '100%',
    padding: '1.25rem',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '2.5rem',
    backgroundColor: status === 'ready' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
    color: status === 'ready' ? '#34D399' : '#FBBF24',
    border: `1px solid ${status === 'ready' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)'}`,
    fontSize: '0.95rem',
  }),
  primaryButton: {
    background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
    border: 'none',
    borderRadius: '12px',
    padding: '1rem 2.5rem',
    color: '#FFF',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.5)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  levelingWrapper: {
    animation: 'fadeIn 0.5s ease-out',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: '1.5rem',
  },
  matrixTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#E2E8F0',
    margin: 0,
  },
  toolbarActions: {
    display: 'flex',
    gap: '1rem',
  },
  activeFocusButton: {
    background: 'rgba(59, 130, 246, 0.2)',
    border: '1px solid #3B82F6',
    color: '#60A5FA',
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.9rem',
    fontWeight: '600',
    transition: 'all 0.2s',
  },
  outlineButton: {
    background: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: '#E2E8F0',
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'background 0.2s',
  },
  primarySettingsButton: {
    background: '#10B981',
    border: 'none',
    color: '#FFF',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.9rem',
    fontWeight: '600',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
  },
  matrixContainer: {
    background: 'rgba(30, 41, 59, 0.4)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    borderRadius: '16px',
    overflowX: 'auto',
    backdropFilter: 'blur(10px)',
  },
  matrixTable: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
  },
  matrixTh: {
    padding: '1.25rem',
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: '#94A3B8',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    fontWeight: '600',
  },
  matrixVendorTh: {
    padding: '1.25rem',
    fontSize: '0.95rem',
    color: '#F8FAFC',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    borderLeft: '1px solid rgba(255, 255, 255, 0.02)',
    fontWeight: '600',
    minWidth: '280px',
  },
  scopeHeaderRow: {
    background: 'rgba(15, 23, 42, 0.8)',
  },
  scopeHeaderCell: {
    padding: '1rem 1.25rem',
    color: '#818CF8',
    fontWeight: '600',
    fontSize: '1rem',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  },
  lineRow: {
    borderBottom: '1px solid rgba(255, 255, 255, 0.02)',
  },
  baseItemCell: {
    padding: '1.25rem',
    verticalAlign: 'top',
    width: '30%',
  },
  baseItemDesc: {
    color: '#E2E8F0',
    fontSize: '0.95rem',
    lineHeight: '1.5',
    marginBottom: '0.5rem',
  },
  baseItemSeq: {
    color: '#64748B',
    fontSize: '0.8rem',
  },
  vendorCell: {
    padding: '1.25rem',
    verticalAlign: 'top',
    borderLeft: '1px solid rgba(255, 255, 255, 0.02)',
    backgroundColor: 'rgba(255, 255, 255, 0)',
    transition: 'background-color 0.2s',
    cursor: 'pointer',
  },
  vendorCellSelected: {
    padding: '1.25rem',
    verticalAlign: 'top',
    borderLeft: '1px solid rgba(255, 255, 255, 0.02)',
    backgroundColor: 'rgba(16, 185, 129, 0.08)',
    boxShadow: 'inset 2px 0 0 #10B981',
    cursor: 'pointer',
  },
  vendorCellEmpty: {
    padding: '1.25rem',
    verticalAlign: 'middle',
    textAlign: 'center',
    borderLeft: '1px solid rgba(255, 255, 255, 0.02)',
    background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.01), rgba(255,255,255,0.01) 10px, transparent 10px, transparent 20px)',
  },
  noBidText: {
    color: '#475569',
    fontSize: '0.85rem',
    fontStyle: 'italic',
  },
  vendorValTotal: {
    color: '#F8FAFC',
    fontSize: '1.15rem',
    fontWeight: '600',
    marginBottom: '0.25rem',
  },
  vendorValDetail: {
    color: '#94A3B8',
    fontSize: '0.85rem',
    marginBottom: '0.75rem',
  },
  vendorNotes: {
    background: 'rgba(245, 158, 11, 0.1)',
    color: '#FCD34D',
    padding: '0.5rem',
    borderRadius: '6px',
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'flex-start',
    lineHeight: '1.4',
    marginBottom: '1rem',
  },
  radioWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 'auto',
    paddingTop: '1rem',
  },
  radioUnselected: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '2px solid #64748B',
    marginRight: '0.5rem',
    transition: 'all 0.2s',
  },
  radioSelected: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '5px solid #10B981',
    marginRight: '0.5rem',
  },
  radioLabel: {
    fontSize: '0.85rem',
    color: '#CBD5E1',
    fontWeight: '500',
  }
};
